import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { helper } from '@myrealtrip/frontend-weblog';
import { media } from '@myrealtrip/modash';
import { ResponsiveImage, StatusTag, Text } from '@myrealtrip/web-ui';
import { WishMark } from '@myrealtrip/wish';

import Show from '~components/Show';
import ReviewRating from '~components/ReviewRating';
import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import { useWebView } from '@myrealtrip/device-detect';

import type { TransportationProduct } from '~models/Transportation/PointToPointTicket';

const { sendClickLog } = helper;

const MOBILE_IMAGE_SIZE = 170;
const DESKTOP_IMAGE_SIZE = 256;

interface Props {
  product: TransportationProduct;
  sectionTitle: string;
  sectionHorizontalIndex: number;
  itemIndex: number;
}

const RecommendationProduct = ({
  product,
  sectionTitle,
  sectionHorizontalIndex,
  itemIndex,
}: Props) => {
  const { colors } = useTheme();

  const {
    thumbnailUrls,
    gid,
    title = '',
    reviewRating = 0,
    reviewCount = 0,
    salePrice,
    productUrls,
  } = product ?? {};

  const { isWebView } = useWebView();

  const handleClickCard = () => {
    if (!productUrls) {
      return;
    }

    sendClickLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: WeblogEventName.RECOMMENDED_PASS,
      itemKind: 'offer',
      itemId: product.gid,
      itemName: product.title,
      sectionTitle: sectionTitle,
      sectionHorizontalIndex,
      itemHorizontalIndex: itemIndex,
    });

    const url = isWebView ? productUrls.APP : productUrls.WEB;
    window.location.href = url;
  };

  return (
    <Container onClick={handleClickCard}>
      <ImageContainer>
        <Show when={product.lowerPriceGuaranteed}>
          <TagContainer>
            <StatusTag label="최저가 보장" variant="white" tint="off" />
          </TagContainer>
        </Show>
        <Image src={thumbnailUrls ? thumbnailUrls[0] : ''} alt="pass product thumbnail" />
        <Wish>
          <WishMark id={gid} showCount={false} size={32} icoBookmarkLineColor={colors.white} />
        </Wish>
      </ImageContainer>
      <Show when={title}>
        <Summary>
          <Title as="p" typography="paragraphBold15" lineClamp={2}>
            {title}
          </Title>
          <Show when={reviewRating && Number(reviewRating) > 0 && reviewCount > 0}>
            <RatingContainer>
              <ReviewRating rating={Number(reviewRating)} count={reviewCount} />
            </RatingContainer>
          </Show>
          <Text typography="headlineBold16">{Math.round(salePrice).toLocaleString()}원</Text>
        </Summary>
      </Show>
    </Container>
  );
};

export default RecommendationProduct;

const Container = styled.div`
  padding: 4px;

  ${media.minMobile} {
    padding: 12px 0 24px;
    margin-right: 12px;
    cursor: pointer;

    &:last-of-type {
      margin-right: 0;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: ${MOBILE_IMAGE_SIZE}px;
  height: ${MOBILE_IMAGE_SIZE}px;
  border-radius: 12px;
  overflow: hidden;

  ${media.minMobile} {
    width: ${DESKTOP_IMAGE_SIZE}px;
    height: ${DESKTOP_IMAGE_SIZE}px;
  }
`;

const TagContainer = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1;
`;

const Image = styled(ResponsiveImage)`
  width: 100%;
  height: 100%;
`;

const Wish = styled.span`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 8px;
`;

const Summary = styled.div`
  width: ${MOBILE_IMAGE_SIZE}px;
  padding: 10px 8px 0;

  ${media.minMobile} {
    width: ${DESKTOP_IMAGE_SIZE}px;
    padding: 10px 4px 0;
  }
`;

const Title = styled(Text)`
  margin: 0 0 2px;
`;

const RatingContainer = styled.div`
  margin: 0 0 2px;
`;
